import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { isRTL } from '../i18n/config';

const LANGUAGES = [
  { code: 'fa', label: 'فارسی', flag: '🇮🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
];

const LanguageDropdown = ({ variant = 'default' }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const currentLang = LANGUAGES.find((l) => l.code === (i18n.language?.split('-')[0] || 'fa')) || LANGUAGES[0];
  const isLight = variant === 'light';

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const computePosition = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const rtl = isRTL(i18n.language);
    setMenuPos({
      top: rect.bottom + 6,
      left: rtl ? rect.left : undefined,
      right: rtl ? undefined : window.innerWidth - rect.right,
      minWidth: Math.max(rect.width, 7 * 16),
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    computePosition();

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(computePosition) : null;
    if (ro && buttonRef.current) ro.observe(buttonRef.current);

    window.addEventListener('scroll', computePosition, true);
    window.addEventListener('resize', computePosition);

    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', computePosition, true);
      window.removeEventListener('resize', computePosition);
    };
  }, [open, i18n.language]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inButton = buttonRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inButton && !inMenu) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <div className="relative shrink-0">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setOpen(!open)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t('nav.languageAria')}
          className={
            isLight
              ? 'flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-white transition-all duration-150 hover:bg-white/15'
              : 'flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-slate-600 transition-all duration-150 hover:bg-black/[0.05] hover:text-ink-graphite'
          }
        >
          <Globe className={`h-4 w-4 shrink-0 ${isLight ? 'text-white' : 'text-current'}`} strokeWidth={1.8} />
          <span className="max-w-[4rem] truncate text-sm font-medium">{currentLang.label}</span>
          <svg
            className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''} ${isLight ? 'text-white/85' : 'text-gray-400'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {open &&
        menuPos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={t('nav.languageAria')}
            className="rounded-xl border border-hairline/60 bg-card-surface py-1 shadow-xl ring-1 ring-black/[0.06]"
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left ?? 'auto',
              right: menuPos.right ?? 'auto',
              minWidth: menuPos.minWidth,
              zIndex: 6000,
            }}
          >
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={currentLang.code === lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  currentLang.code === lang.code
                    ? 'bg-sky-wash font-medium text-signal'
                    : 'text-ink-graphite hover:bg-sky-wash/60'
                }`}
              >
                <span className="text-base">{lang.flag}</span>
                <span>{lang.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default LanguageDropdown;
